<script setup>
import IconScales from '@manseImages/harmony/icons/icon-scales.png';
import IconPuzzle from '@manseImages/harmony/icons/icon-puzzle.png';
import IconFlashOn from '@manseImages/harmony/icons/icon-flash-on.png';
import IconSignpost from '@manseImages/harmony/icons/icon-signpost.png';
import IconMagnetic from '@manseImages/harmony/icons/icon-magnetic.png';
import IconOceanWave from '@manseImages/harmony/icons/icon-ocean-wave.png';
import IconSoccerBall from '@manseImages/harmony/icons/icon-soccer-ball.png';
import IconMusic from '@manseImages/harmony/icons/icon-music.png';
import IconMonitor from '@manseImages/harmony/icons/icon-monitor.png';
import IconTrust from '@manseImages/harmony/icons/icon-trust.png';
import IconTeddyBear from '@manseImages/harmony/icons/icon-teddy-bear.png';
import IconMarketShare from '@manseImages/harmony/icons/icon-market-share.png';
import IconHeartArrow from '@manseImages/harmony/icons/icon-heart-arrow.png';
import IconHighFive from '@manseImages/harmony/icons/icon-high-five.png';
import IconChatBubble from '@manseImages/harmony/icons/icon-chat-bubble.png';

const fullWidthMode = inject('fullWidthMode', false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));

const props = defineProps({
  hData: Object,
  userName: String,
  categoryId: {
    type: String,
    default: 'lover',
  },
  hasPermission: Boolean,
});

/** 카테고리별 아이콘 배열 (피그마 순서 기준) */
const CATEGORY_ICON_MAP = {
  lover: [IconScales, IconPuzzle, IconFlashOn, IconSignpost, IconMagnetic],
  friend: [IconScales, IconSoccerBall, IconMusic, IconSignpost, IconTeddyBear],
  business: [IconChatBubble, IconPuzzle, IconMonitor, IconSignpost, IconMarketShare],
  family: [IconTrust, IconChatBubble, IconHeartArrow, IconSignpost, IconHighFive],
};
const ICON_LIST_DEFAULT = [IconScales, IconPuzzle, IconFlashOn, IconSignpost, IconMagnetic, IconOceanWave];

const currentIconList = computed(() => {
  const catKey = props.categoryId?.toLowerCase() || 'lover';
  return CATEGORY_ICON_MAP[catKey] || ICON_LIST_DEFAULT;
});

/** 아이콘 조회 헬퍼 — iconIndex 기반 */
const getIcon = (item) => {
  const icons = currentIconList.value;
  if (item.iconIndex != null) return icons[item.iconIndex] || ICON_LIST_DEFAULT[item.iconIndex];
  return null;
};

/** 타이틀 조회 헬퍼 */
const getTitle = (item) => {
  return item.title;
};

/** 지표 상세 분석 데이터 (왼쪽) */
const indicators = computed(() => props.hData?.지표상세분석 || []);

/** 관계 스타일 분석 데이터 (오른쪽) */
const relationStyles = computed(() => props.hData?.관계스타일분석 || []);

/** 배지 → CSS modifier 키 매핑 */
const BADGE_KEY_MAP = {
  최고: 'best',
  편안: 'comfort',
  무난: 'fine',
  주의: 'caution',
}
const getBadgeKey = (badge) => BADGE_KEY_MAP[badge] || 'fine'
const getBadgeIcon = (badge) => {
  const key = getBadgeKey(badge)
  if (key === 'best') return '🌟'
  if (key === 'comfort') return '🫧'
  if (key === 'fine') return '☀️'
  return '⚠️'
}

/** 별점 렌더링 헬퍼 — 1~5 범위로 별 배열 반환 */
const getStars = (rating) => {
  const filled = Math.floor(rating);
  const hasHalf = rating - filled >= 0.5;
  const result = [];
  for (let i = 0; i < 5; i++) {
    if (i < filled) result.push('filled');
    else if (i === filled && hasHalf) result.push('half');
    else result.push('empty');
  }
  return result;
};
</script>

<template>
  <VRow class="full-height">
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="indicator-card full-height right">
        <div class="indicator-con">
          <div
            v-for="item in indicators"
            :key="item.id"
            class="indicator-detail-card"
          >
            <!-- 카드 헤더: 아이콘 + 타이틀 + 별점 -->
            <div class="indicator-detail-card__header">
              <div class="indicator-detail-card__title-wrap">
                <img
                  :src="getIcon(item)"
                  :alt="getTitle(item)"
                  class="indicator-detail-card__icon"
                />
                <span class="indicator-detail-card__title">
                  {{ getTitle(item) }}
                </span>
              </div>
              <div class="indicator-detail-card__rating">
                <svg
                  v-for="(star, sIdx) in getStars(item.rating)"
                  :key="sIdx"
                  class="indicator-star"
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                >
                  <defs v-if="star === 'half'">
                    <linearGradient :id="'halfGrad-' + item.id + '-' + sIdx">
                      <stop offset="50%" stop-color="#F5A623" />
                      <stop offset="50%" stop-color="#E0E0E0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M7.5 0.5L9.5 5.2L14.5 5.6L10.7 8.9L11.8 13.8L7.5 11.2L3.2 13.8L4.3 8.9L0.5 5.6L5.5 5.2L7.5 0.5Z"
                    :fill="star === 'filled' ? '#F5A623' : star === 'half' ? `url(#halfGrad-${item.id}-${sIdx})` : '#E0E0E0'"
                  />
                </svg>
              </div>
            </div>

            <!-- 메인 설명 -->
            <div class="indicator-detail-card__body">
            <div class="indicator-detail-card__desc-wrap">
              <template v-if="item.detailInterpretation?.pairs?.length || item.detailInterpretation?.overall">
                <!-- 개별 궁합 카드 (배지별 색상) -->
                <div
                  v-for="(pair, pIdx) in item.detailInterpretation?.pairs"
                  :key="pIdx"
                  class="interpret-block"
                  :class="`interpret-block--${getBadgeKey(pair.badge)}`"
                >
                  <div class="interpret-block__head">
                    <span
                      class="interpret-block__badge"
                      :class="`interpret-block__badge--${getBadgeKey(pair.badge)}`"
                    >
                      {{ getBadgeIcon(pair.badge) }} {{ pair.badge }}
                    </span>
                    <span v-if="pair.pair" class="interpret-block__pair">{{ pair.pair }}</span>
                    <span
                      v-if="pair.score"
                      class="interpret-block__score"
                      :class="`interpret-block__score--${getBadgeKey(pair.badge)}`"
                    >{{ Math.round(pair.score) }}점</span>
                  </div>
                  <p v-if="pair.intro" class="interpret-block__intro" v-html="pair.intro"></p>
                  <p v-if="pair.situation" class="interpret-block__text" v-html="pair.situation"></p>
                  <p v-if="pair.action" class="interpret-block__action">
                    <span class="interpret-block__action-label">💡 이렇게 해보세요</span>
                    <span class="interpret-block__action-text" v-html="pair.action"></span>
                  </p>
                </div>

                <!-- 종합 -->
                <div v-if="item.detailInterpretation?.overall" class="interpret-overall">
                  <span class="interpret-overall__label">📝 한 줄 정리</span>
                  <p class="interpret-overall__text" v-html="item.detailInterpretation.overall"></p>
                </div>
              </template>
              <p v-else class="indicator-detail-card__main-desc" v-html="item.mainDesc"></p>
            </div>

            <!-- 이런 이유예요 / 함께 해볼 것들 -->
            <div class="indicator-detail-card__sections">
              <!-- 이런 이유예요 -->
              <div v-if="item.situation" class="indicator-section indicator-section--situation">
                <p class="indicator-section__label">이런 이유예요</p>
                <p class="indicator-section__text" v-html="item.situation"></p>
              </div>

              <!-- 함께 해볼 것들 -->
              <div v-if="item.practices?.length" class="indicator-section indicator-section--practice">
                <p class="indicator-section__label">함께 해볼 것들</p>
                <p
                  v-for="(practice, pIdx) in item.practices"
                  :key="pIdx"
                  class="indicator-section__text"
                  v-html="practice"
                >
                </p>
              </div>

            </div>

            <!-- 태그 -->
            <div v-if="item.tags?.length" class="indicator-detail-card__tags">
              <span
                v-for="(tag, tIdx) in item.tags"
                :key="tIdx"
                class="indicator-tag"
              >
                {{ tag }}
              </span>
            </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>

    <!-- 오른쪽 페이지: 관계 스타일 분석 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="indicator-card full-height">
        <div class="indicator-con">
          <div
            v-for="item in relationStyles"
            :key="item.id"
            class="indicator-detail-card"
          >
            <!-- 카드 헤더: 아이콘 + 타이틀 + 별점 -->
            <div class="indicator-detail-card__header">
              <div class="indicator-detail-card__title-wrap">
                <img
                  :src="getIcon(item)"
                  :alt="getTitle(item)"
                  class="indicator-detail-card__icon"
                />
                <span class="indicator-detail-card__title">
                  {{ getTitle(item) }}
                </span>
              </div>
              <div class="indicator-detail-card__rating">
                <svg
                  v-for="(star, sIdx) in getStars(item.rating)"
                  :key="sIdx"
                  class="indicator-star"
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                >
                  <defs v-if="star === 'half'">
                    <linearGradient :id="'halfGrad-r-' + item.id + '-' + sIdx">
                      <stop offset="50%" stop-color="#F5A623" />
                      <stop offset="50%" stop-color="#E0E0E0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M7.5 0.5L9.5 5.2L14.5 5.6L10.7 8.9L11.8 13.8L7.5 11.2L3.2 13.8L4.3 8.9L0.5 5.6L5.5 5.2L7.5 0.5Z"
                    :fill="star === 'filled' ? '#F5A623' : star === 'half' ? `url(#halfGrad-r-${item.id}-${sIdx})` : '#E0E0E0'"
                  />
                </svg>
              </div>
            </div>

            <!-- 메인 설명 -->
            <div class="indicator-detail-card__body">
            <div class="indicator-detail-card__desc-wrap">
              <template v-if="item.detailInterpretation?.pairs?.length || item.detailInterpretation?.overall">
                <!-- 개별 궁합 카드 (배지별 색상) -->
                <div
                  v-for="(pair, pIdx) in item.detailInterpretation?.pairs"
                  :key="pIdx"
                  class="interpret-block"
                  :class="`interpret-block--${getBadgeKey(pair.badge)}`"
                >
                  <div class="interpret-block__head">
                    <span
                      class="interpret-block__badge"
                      :class="`interpret-block__badge--${getBadgeKey(pair.badge)}`"
                    >
                      {{ getBadgeIcon(pair.badge) }} {{ pair.badge }}
                    </span>
                    <span v-if="pair.pair" class="interpret-block__pair">{{ pair.pair }}</span>
                    <span
                      v-if="pair.score"
                      class="interpret-block__score"
                      :class="`interpret-block__score--${getBadgeKey(pair.badge)}`"
                    >{{ Math.round(pair.score) }}점</span>
                  </div>
                  <p v-if="pair.intro" class="interpret-block__intro" v-html="pair.intro"></p>
                  <p v-if="pair.situation" class="interpret-block__text" v-html="pair.situation"></p>
                  <p v-if="pair.action" class="interpret-block__action">
                    <span class="interpret-block__action-label">💡 이렇게 해보세요</span>
                    <span class="interpret-block__action-text" v-html="pair.action"></span>
                  </p>
                </div>

                <!-- 종합 -->
                <div v-if="item.detailInterpretation?.overall" class="interpret-overall">
                  <span class="interpret-overall__label">📝 한 줄 정리</span>
                  <p class="interpret-overall__text" v-html="item.detailInterpretation.overall"></p>
                </div>
              </template>
              <p v-else class="indicator-detail-card__main-desc" v-html="item.mainDesc"></p>
            </div>

            <!-- 이런 이유예요 / 함께 해볼 것들 -->
            <div class="indicator-detail-card__sections">
              <!-- 이런 이유예요 -->
              <div v-if="item.situation" class="indicator-section indicator-section--situation">
                <p class="indicator-section__label">이런 이유예요</p>
                <p class="indicator-section__text" v-html="item.situation"></p>
              </div>

              <!-- 함께 해볼 것들 -->
              <div v-if="item.practices?.length" class="indicator-section indicator-section--practice">
                <p class="indicator-section__label">함께 해볼 것들</p>
                <p
                  v-for="(practice, pIdx) in item.practices"
                  :key="pIdx"
                  class="indicator-section__text"
                  v-html="practice"
                >
                </p>
              </div>

            </div>

            <!-- 태그 -->
            <div v-if="item.tags?.length" class="indicator-detail-card__tags">
              <span
                v-for="(tag, tIdx) in item.tags"
                :key="tIdx"
                class="indicator-tag"
              >
                {{ tag }}
              </span>
            </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss" scoped>
@import '@/assets/nangman_style/_analysis_main_color.scss';

.indicator-card {
  border-radius: 0 !important;
  box-shadow: none !important;
  padding: 0;
  overflow-y: auto;

  &.right {
    border-right: 1px solid var(--Gray-300, #ebebeb);
  }
}

.indicator-con {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  justify-content: center;
  padding: 24px 18px;
  width: 100%;
  box-sizing: border-box;
}

/* 상세 분석 카드 */
.indicator-detail-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 14px 16px;
    background: #fffbf5;
    border-bottom: 1px solid #f0e8dc;
    box-sizing: border-box;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    width: 100%;
    box-sizing: border-box;
  }

  &__title-wrap {
    display: flex;
    gap: 5px;
    align-items: center;
  }

  &__icon {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }

  &__title {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    white-space: nowrap;
  }

  &__rating {
    display: flex;
    gap: 0;
    align-items: center;
  }

  &__desc-wrap {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }

  &__main-desc {
    color: var(--Gray-700, #575757);
    font-size: 15px;
    font-weight: 500;
    line-height: 24px;
    white-space: pre-line;
  }

  &__sections {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    color: var(--Gray-700, #575757);
    font-size: 15px;
    line-height: 24px;
  }

  &__tags {
    display: flex;
    gap: 5px;
    align-items: center;
  }
}

/* 별점 아이콘 */
.indicator-star {
  flex-shrink: 0;
}

/* 섹션 공통 */
.indicator-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;

  &__label {
    font-weight: 700;
    white-space: nowrap;
  }

  &__text {
    font-weight: 400;
  }

  /* 이런 이유예요 */
  &--situation {
    background: #fffaeb;
  }

  /* 함께 해볼 것들 */
  &--practice {
    background: #f2f8ff;
  }

  /* 최고쌍 / 최저쌍 */
  &--pair {
    background: var(--gray-100-white, #fff);
    border: 1px solid var(--Gray-300, #ebebeb);
    border-radius: 8px;
    gap: 8px;
  }
}

/* 최고쌍 / 최저쌍 행 */
.pair-row {
  display: flex;
  align-items: center;
  gap: 8px;

  &__label {
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
  }

  &__name {
    flex: 1;
    font-size: 14px;
    font-weight: 500;
    color: var(--Gray-700, #575757);
  }

  &__score {
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
  }

  &--best &__score {
    color: #4E80D6;
  }

  &--worst &__score {
    color: #FF6B6B;
  }
}

/* 해석상세 블록 */
.interpret-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid transparent;
  width: 100%;
  box-sizing: border-box;

  /* 배지 4단계 — 배터리 게이지(그린→라임→옐로우→코랄) */
  &--best {
    background: linear-gradient(180deg, #ddf3dc 0%, #ffffff 100%);
    border-color: #a8dba3;
  }

  &--comfort {
    background: linear-gradient(180deg, #dfeefc 0%, #ffffff 100%);
    border-color: #a9c8ec;
  }

  &--fine {
    background: linear-gradient(180deg, #fff4d6 0%, #ffffff 100%);
    border-color: #f0d99a;
  }

  &--caution {
    background: linear-gradient(180deg, #fde0dc 0%, #ffffff 100%);
    border-color: #f2b4ac;
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 15px;
    font-weight: 700;
    line-height: 18px;
    white-space: nowrap;
    color: #fff;

    &--best {
      background: #5aaf52;
    }

    &--comfort {
      background: #4a8fd6;
    }

    &--fine {
      background: #d9a93a;
    }

    &--caution {
      background: #e57368;
    }
  }

  &__pair {
    flex: 1;
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
  }

  &__score {
    font-size: 15px;
    font-weight: 700;
    white-space: nowrap;

    &--best {
      color: #2f7a2a;
    }

    &--comfort {
      color: #2f5ba8;
    }

    &--fine {
      color: #8a6a1f;
    }

    &--caution {
      color: #b83a2e;
    }
  }

  &__intro {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 600;
    line-height: 22px;
  }

  &__text {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 400;
    line-height: 22px;
  }

  &__action {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 4px;
    padding: 10px 12px;
    border-radius: 4px;
    background: #FFF;
    border: 1px dashed #BF8953;
  }

  &__action-label {
    font-size: 15px;
    font-weight: 700;
    color: #b47a37;
    line-height: 24px;
  }

  &__action-text {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 500;
    line-height: 24px;
  }
}

/* 종합 한줄 */
.interpret-overall {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
  padding: 14px 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, #fff4dc 0%, #fff9ec 100%);
  border: 1px solid #f0d9a8;
  box-shadow: 0 2px 6px rgba(245, 166, 35, 0.08);

  &__label {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    padding: 3px 10px;
    border-radius: 999px;
    background: #f5a623;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    line-height: 18px;
    white-space: nowrap;
  }

  &__text {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 400;
    line-height: 23px;
  }
}

/* 태그 */
.indicator-tag {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 3px 6px;
  border-radius: 4px;
  background: var(--Gray-200, #F8F8F8);
  color: #575757;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
}
</style>
